import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../dataManagement/uiDb/UiDb";
import { fetchUserSubscriptions, subscribeBusiness } from "../services/thunks/SubscriptionThunk";
import { subscriptionOpKey } from "../dataManagement/uiDb/SubscriptionsDb";
import { getAutopayReadiness, isAutopayLocked, lockedMessageFor } from "../services/helpers/AutopayGateHelper";
import BusinessApiService from "../services/apiServices/BusinessApiService";
import { verifyCashfreeAvailable, openCashfreeCheckout } from "../services/helpers/cashfreeCheckout";
import { ANDROID_PLAY_STORE_URL, isAndroidAppTooOldForAutopay } from "../services/helpers/platform";

interface SubscribeResult {
  success: boolean;
  error?: string;
}

interface UseSubscribeBusinessResult {
  /** Verifies Cashfree SDK availability, creates a backend subscription, then
   *  opens the Cashfree payment flow. Fails fast (before touching the backend)
   *  if the SDK is blocked — preventing dangling ACTION_REQUIRED subscriptions. */
  subscribe: (businessId: string, planCode?: string) => Promise<SubscribeResult>;
  /** True during the entire subscribe flow — SDK verify, backend call, and
   *  checkout — not just the backend call phase. Keeps the button disabled
   *  throughout so the user gets consistent feedback. */
  isSubscribing: (businessId: string) => boolean;
  /** Returns errors from all failure paths: backend rejection AND SDK/Cashfree
   *  failures that bypass Redux (e.g. SDK blocked by ad-blocker). */
  getSubscribeError: (businessId: string) => string | null;
}

export function useSubscribeBusiness(): UseSubscribeBusinessResult {
  const dispatch: AppDispatch = useDispatch();
  const loadingMap = useSelector((state: RootState) => state.subscriptions.loadingMap);
  const errorMap = useSelector((state: RootState) => state.subscriptions.errorMap);
  const myBusinesses = useSelector((state: RootState) => state.business.myBusinesses);
  const currentBusiness = useSelector((state: RootState) => state.business.currentBusiness);

  // Local state covers the SDK verify + checkout phases that Redux doesn't know about.
  const [localInFlight, setLocalInFlight] = useState<Set<string>>(new Set());
  const [localErrors, setLocalErrors] = useState<Record<string, string | null>>({});

  // Ref-based guard prevents concurrent calls within the same component instance —
  // state reads in event handlers are captured at render time and can't reliably
  // prevent rapid double-taps before the first re-render.
  const isInFlight = useRef(false);

  const subscribe = async (
    businessId: string,
    planCode: string = "plan"
  ): Promise<SubscribeResult> => {
    if (isInFlight.current) return { success: false };

    isInFlight.current = true;
    setLocalInFlight((prev) => new Set(prev).add(businessId));
    setLocalErrors((prev) => ({ ...prev, [businessId]: null }));

    try {
      const known =
        currentBusiness?.businessId === businessId
          ? currentBusiness
          : myBusinesses.find((b) => b.businessId === businessId);
      let business = known;
      if (!business) {
        try {
          business = await BusinessApiService.getBusinessById(businessId);
        } catch {
          const message = "Couldn't verify your business page. Please try again.";
          setLocalErrors((prev) => ({ ...prev, [businessId]: message }));
          return { success: false, error: message };
        }
      }
      if (isAutopayLocked(business)) {
        const message = lockedMessageFor(getAutopayReadiness(business));
        setLocalErrors((prev) => ({ ...prev, [businessId]: message }));
        return { success: false, error: message };
      }

      // Old Android app builds hand Cashfree's checkout to the phone's browser and
      // lose the session ("subs_session_id is not present or is invalid"). Send the
      // owner to the Play Store instead of creating a session that can't work.
      // The app routes this non-ninefinds URL to the Play Store app.
      if (isAndroidAppTooOldForAutopay()) {
        const message =
          "Please update the NineFinds app from the Play Store to set up autopay.";
        setLocalErrors((prev) => ({ ...prev, [businessId]: message }));
        window.location.href = ANDROID_PLAY_STORE_URL;
        return { success: false, error: message };
      }

      // Verify SDK loads before touching the backend — prevents dangling
      // ACTION_REQUIRED subscriptions when the SDK is blocked by an
      // ad-blocker, CSP issue, or transient network failure.
      await verifyCashfreeAvailable();

      const result = await dispatch(subscribeBusiness({ businessId, planCode }));
      if (!subscribeBusiness.fulfilled.match(result)) {
        return { success: false, error: result.payload as string };
      }

      const sub = result.payload;
      if (sub.subscriptionSessionId) {
        await openCashfreeCheckout(sub.subscriptionSessionId);
        // With the _self redirect the page navigates away here. If Cashfree returns without
        // leaving (cancel, error), refresh the cards ourselves.
        dispatch(fetchUserSubscriptions());
      }
      return { success: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong. Please try again.";
      setLocalErrors((prev) => ({ ...prev, [businessId]: msg }));
      return { success: false, error: msg };
    } finally {
      isInFlight.current = false;
      setLocalInFlight((prev) => {
        const next = new Set(prev);
        next.delete(businessId);
        return next;
      });
    }
  };

  const isSubscribing = (businessId: string): boolean =>
    localInFlight.has(businessId) ||
    !!loadingMap[subscriptionOpKey("subscribeBusiness", businessId)];

  const getSubscribeError = (businessId: string): string | null =>
    localErrors[businessId] ??
    errorMap[subscriptionOpKey("subscribeBusiness", businessId)] ??
    null;

  return { subscribe, isSubscribing, getSubscribeError };
}
